import { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { getAllWarehouses } from '../../../services/apis';
import useFormatMessage from '../../../utils/useFormatMessage';

const Tarihce = ({ data }) => {
    const { t } = useFormatMessage();
    const [warehouse, setWarehouse] = useState([]);
    const [zone, setZone] = useState([]);

    useEffect(() => {
        fetchWareHouse();
        fetchZone();
    }, []);

    const fetchWareHouse = async () => {
        try {
            const response = await getAllWarehouses();
            setWarehouse(response || []);
        } catch (err) {
            console.log(err);
            setWarehouse([]);
        }
    };

    const fetchZone = async (id) => {
        try {
            const response = await api.post("/zone/getAll", { warehouse_id: id });
            setZone(response?.data?.data || []);
        } catch (err) {
            console.log(err);
            setZone([]);
        }
    };

    let excludedFields = [];
    let unvisibleColumns = ["original invoice number", "client code", "client invoice date", "client name", "sase", "marka", "commercial denomination name", "car line", "commercial denomination name", "colour code", "colour name", "gemi adı", "gemi varış tarihi"];
    let tanim = ["marka", "commercial denomination code", "commercial denomination name", "car line", "colour code", "colour name", "gemi adı", "gemi varış tarihi"];

    const processedData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        return data.map(item => {
            if (!item) return {};

            const processedItem = { ...item };
            const clmData = {};
            let clmIndex = 1;

            if (item.data) {
                // GÜMRÜK İŞLEMİ için sadece customs_clearance verilerini işle
                if (item?.operation === "GÜMRÜK İŞLEMİ") {
                    if (item.data.customs_clearance) {
                        Object.entries(item.data.customs_clearance).forEach(([key, value]) => {
                            if (!key || excludedFields.includes(key)) return;

                            clmData[`clm${clmIndex}`] = {
                                key,
                                value: value ?? ''
                            };
                            clmIndex++;
                        });
                    }
                } else {
                    // Diğer operasyonlar için mevcut logic
                    Object.entries(item.data).forEach(([key, value]) => {
                        if (!key || excludedFields.includes(key)) return;

                        if (typeof value === 'object') {
                            if (key === 'warning' && value) {
                                clmData[`clm${clmIndex}`] = {
                                    key: 'warning_info',
                                    value: value?.info ?? ''
                                };
                                clmIndex++;

                                clmData[`clm${clmIndex}`] = {
                                    key: 'warning_date',
                                    value: value?.date ?? ''
                                };
                                clmIndex++;
                            } else if (key === 'ship_unload' && value) {
                                clmData[`clm${clmIndex}`] = {
                                    key: 'unload_date',
                                    value: value?.unload_date ?? ''
                                };
                                clmIndex++;
                            } else {
                                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                                    if (!nestedKey || excludedFields.includes(`${key}.${nestedKey}`)) return;

                                    if ((item?.operation?.includes?.("ŞASE TANIMLAMA") && tanim.includes(key)) ||
                                        (!item?.operation?.includes?.("ŞASE TANIMLAMA") && !tanim.includes(key))) {
                                        clmData[`clm${clmIndex}`] = {
                                            key: `${key}.${nestedKey}`,
                                            value: nestedValue ?? ''
                                        };
                                        clmIndex++;
                                    }
                                });
                            }
                        } else {
                            if ((item?.operation?.includes?.("ŞASE TANIMLAMA") && tanim.includes(key)) ||
                                (!item?.operation?.includes?.("ŞASE TANIMLAMA") && !tanim.includes(key))) {
                                clmData[`clm${clmIndex}`] = {
                                    key,
                                    value: value ?? ''
                                };
                                clmIndex++;
                            }
                        }
                    });
                }
            }

            return {
                ...processedItem,
                ...clmData
            };
        }).filter(Boolean);
    }, [data, excludedFields]);

    const getStatusDisplay = (data) => {
        const action = data?.action;
        if (!action) return '';
        if (action === "created") return t("EKLENDİ");
        if (action === "deleted") return t("SİLİNDİ");
        if (action === "updated") return t("GÜNCELLENDİ");
        return action;
    };

    const calculateFilterExpression = (filterValue, selectedFilterOperation, currentField) => {
        if (typeof filterValue !== 'string') return null;

        const searchValue = filterValue.toLowerCase().trim();

        return [
            [currentField, 'contains', searchValue],
            'or',
            [(rowData) => {
                const clmData = rowData?.[currentField];
                if (!clmData) return false;

                const keyMatch = clmData.key?.toString().toLowerCase().includes(searchValue);
                const valueMatch = clmData.value?.toString().toLowerCase().includes(searchValue);

                return keyMatch || valueMatch;
            }]
        ];
    };

    const formatColumnValue = (clmData, data) => {
        if (!clmData?.key) return '';

        if ((data?.operation?.includes?.("ŞASE TANIMLAMA") && !tanim.includes(clmData.key)) ||
            (!data?.operation?.includes?.("ŞASE TANIMLAMA") && tanim.includes(clmData.key))) {
            return '';
        }

        if (clmData.key === "antrepo_zone_id" && clmData.value) {
            const zoneItem = zone && zone.find(item => item?.id === parseInt(clmData.value));
            return zoneItem?.zone_name ? `${t(clmData.key)}: ${zoneItem.zone_name}` : '';
        }

        if (clmData.key === "warning_info") {
            return `${t("Uyarı")}: ${clmData.value}`;
        }

        if (clmData.key === "warning_date" || clmData.key === "unload_date") {
            return `${t(clmData.key)}: ${clmData.value}`;
        }

        if ((clmData.key === "from_warehouse_id" || clmData.key === "to_warehouse_id") && clmData.value) {
            const ware = warehouse && warehouse.find(item => item?.id === clmData.value);
            return ware?.warehouse_name ? `${t(clmData.key)}: ${ware.warehouse_name}` : '';
        }

        if (clmData.key === "process_type") return '';

        return unvisibleColumns.includes(clmData.key)
            ? `${clmData.value ?? ''}`
            : `${t(clmData.key)}: ${clmData.value ?? ''}`;
    };

    const renderDynamicColumns = () => {
        return [...Array(10)].map((_, index) => (
            <Column
                key={index}
                caption={t(`Clm${index + 1}`)}
                dataField={`clm${index + 1}`}
                width={"auto"}
                calculateDisplayValue={(data) => formatColumnValue(data?.[`clm${index + 1}`], data)}
                calculateFilterExpression={(filterValue, selectedFilterOperation) =>
                    calculateFilterExpression(filterValue, selectedFilterOperation, `clm${index + 1}`)}
                allowFiltering={true}
            />
        ));
    };

    return (
        <div className="h-full w-full overflow-hidden">
            <DatagridCustom
                width="100%"
                height="100%"
                columnAutoWidth={true}
                allowColumnResizing={true}
                showBorders={true}
                dataSource={processedData}
                style={{
                    maxHeight: "450px",
                }}
            >
                <SearchPanel visible={true} width={240} placeholder="Ara..." />
                <Column caption={t("Dosya Adı")} dataField="file_name" />
                <Column
                    caption={t("Yükleme Tarih")}
                    dataField="process_date"
                    dataType="datetime"
                    format="dd/MM/yyyy HH:mm"
                />
                <Column caption={t("Kullanıcı")} dataField="username" />
                {/* <Column
                    caption={t("Durum Açıklama")}
                    dataField="action"
                    calculateDisplayValue={getStatusDisplay}
                /> */}
                <Column caption={t("Olay Adı")} dataField="operation" />
                {renderDynamicColumns()}
                <Scrolling
                    mode="virtual"
                    showScrollbar="always"
                    useNative={false}
                    columnRenderingMode='virtual'
                />
            </DatagridCustom>
        </div>
    );
};

export default Tarihce;